import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

interface CustomModalProps {
  header?: React.ReactNode;
  message?: React.ReactNode;
  logo?: string;
  hint?: React.ReactNode;
  firstChoice?: React.ReactNode;
  secondChoice?: React.ReactNode;
  cancel?: React.ReactNode;
  isOpen: boolean;
  handleFirstChoice?: () => void;
  handleSecondChoice?: () => void;
  handleCancel?: () => void;
  GroupName?: React.ReactNode;
  isFirstChoiceDisabled?: boolean;
}

const noop = () => {};

/**
 * `maxWidth={false}` keeps the paper sized by the 550px wrapper Box rather
 * than by Dialog's breakpoint scale.
 *
 * Esc and a backdrop click both reach `handleCancel`, which is a plain close
 * at every call site.
 */
const CustomModal = ({
  header,
  message,
  logo,
  hint,
  firstChoice,
  secondChoice,
  cancel,
  isOpen,
  handleFirstChoice = noop,
  handleSecondChoice = noop,
  handleCancel = noop,
  GroupName,
  isFirstChoiceDisabled = false,
}: CustomModalProps) => {
  const headerId = React.useId();

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      maxWidth={false}
      aria-labelledby={header ? headerId : undefined}
    >
      <DialogContent>
        <Box sx={{ textAlign: "center", width: 550, maxWidth: "100%" }}>
          {header && (
            <Typography
              id={headerId}
              variant="h3"
              sx={{ fontWeight: "bold", fontSize: "1.3em", mb: "1em" }}
            >
              {header}
              <br />
              {GroupName}
            </Typography>
          )}
          {message && (
            <>
              {logo && (
                <Box
                  component="img"
                  src={logo}
                  alt="logo"
                  sx={{
                    width: 75,
                    height: 75,
                    // The old markup carried className="logo custom-modal__logo".
                    // `.custom-modal__logo` supplied the size; the global `.logo`
                    // rule in _theme.scss supplied these two, so they move inline.
                    objectFit: "cover",
                    border: "1px solid",
                    borderColor: "grey.50",
                  }}
                />
              )}{" "}
              <Typography sx={{ fontSize: "1.1em", mb: "1em" }}>
                {message}
              </Typography>
            </>
          )}
          {hint && (
            <Typography sx={{ color: "grey.800", fontStyle: "italic", mb: "1em" }}>
              {hint}
            </Typography>
          )}

          <Stack
            direction="row"
            sx={{
              width: 1,
              my: "10px",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            {cancel && (
              <Button
                onClick={handleCancel}
                variant="text"
                sx={{ color: "grey.600", mr: "15px" }}
              >
                {cancel}
              </Button>
            )}

            {secondChoice && (
              <Button
                onClick={handleSecondChoice}
                variant="outlined"
                sx={{
                  p: "10px",
                  minWidth: 100,
                  mr: "20px",
                  color: "grey.600",
                  borderColor: "grey.600",
                }}
              >
                {secondChoice}
              </Button>
            )}

            {firstChoice && (
              <Button
                disabled={isFirstChoiceDisabled}
                data-testid="customModal_firstChoice"
                onClick={handleFirstChoice}
                variant="contained"
                sx={{
                  p: "10px",
                  minWidth: 100,
                  bgcolor: "primary.light",
                  color: "common.white",
                }}
              >
                {firstChoice}
              </Button>
            )}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CustomModal;
